// The pricing model's forward pass.
//
// A faithful port of the scoring in PricingTool's index.html, which is itself a
// reimplementation of export_model.py's forward(). The model is a ridge
// regression on log price, so once the preprocessing is unrolled the whole
// thing is a dot product - which is why an 8KB spec and forty lines of
// arithmetic replace a service call.
//
// Do not "improve" the arithmetic here. It has to agree with the Python that
// trained it; PricingTool's copy was checked against that Python over 500
// randomised inputs, including unknown categories and every combination of
// missing inputs, to a maximum difference of 7e-15. Any change to this file
// wants the same treatment.

import spec from './model.json';

export type ModelSpec = {
  categorical: {
    column: string;
    kept_levels: string[];
    infrequent_levels: string[];
    has_infrequent_column: boolean;
  }[];
  numeric: string[];
  indicator_for: string[];
  impute_medians: number[];
  scaler_mean: number[];
  scaler_scale: number[];
  coef: number[];
  intercept: number;
  smearing: number;
  residual_quantiles: Record<string, number>;
  training_price_quantiles: Record<string, number>;
  feature_names: string[];
};

export const SPEC = spec as unknown as ModelSpec;

/**
 * One scored scope. Values are model column names; null means "not stated",
 * which is a real answer to the model rather than a missing one - three of the
 * columns carry their own indicator for it.
 */
export type ScopeInput = Record<string, string | number | null | undefined>;

export type Driver = {
  /** Model feature name, e.g. "room_bedroom" or "flag_asbestos not stated". */
  name: string;
  /** Multiplier on the price, e.g. 1.18 for +18%. */
  multiplier: number;
};

export type Estimate = {
  /** Point estimate in dollars. */
  mid: number;
  /** Half of comparable jobs landed between these. */
  lo50: number;
  hi50: number;
  /** Eight in ten did. */
  lo80: number;
  hi80: number;
  /** What moved the number most, largest first. */
  drivers: Driver[];
};

type Part = { value: number; name: string; on: boolean };

function designRow(input: ScopeInput): { x: number[]; parts: Part[] } {
  const x: number[] = [];
  const parts: Part[] = [];

  for (const c of SPEC.categorical) {
    const raw = input[c.column];
    const v = raw == null ? null : String(raw);
    const width = c.kept_levels.length + (c.has_infrequent_column ? 1 : 0);
    const seg = new Array<number>(width).fill(0);
    const i = c.kept_levels.indexOf(v as string);
    if (i >= 0) seg[i] = 1;
    else if (c.has_infrequent_column && c.infrequent_levels.includes(v as string)) {
      seg[c.kept_levels.length] = 1;
    }
    // An unknown level stays all-zero, matching sklearn's handle_unknown="ignore".
    for (const s of seg) {
      parts.push({ value: s, name: `${c.column}=${v}`, on: s === 1 && (i >= 0 || c.has_infrequent_column) });
    }
    x.push(...seg);
  }

  const nums = SPEC.numeric;
  for (let i = 0; i < nums.length; i++) {
    const raw = input[nums[i]!];
    const v = raw == null ? SPEC.impute_medians[i]! : Number(raw);
    const z = (Math.log1p(v) - SPEC.scaler_mean[i]!) / SPEC.scaler_scale[i]!;
    x.push(z);
    parts.push({ value: z, name: nums[i]!, on: raw != null && Number(raw) > 0 });
  }

  // The missing indicators. "Not stated" is its own signal, which is why an
  // unanswered square footage is not the same as a zero one.
  for (let j = 0; j < SPEC.indicator_for.length; j++) {
    const col = SPEC.indicator_for[j]!;
    const missing = input[col] == null ? 1 : 0;
    const k = nums.length + j;
    const z = (Math.log1p(missing) - SPEC.scaler_mean[k]!) / SPEC.scaler_scale[k]!;
    x.push(z);
    parts.push({ value: z, name: `${col} not stated`, on: missing === 1 });
  }

  return { x, parts };
}

/** Drivers below this move the price by less than about 1%. */
const DRIVER_THRESHOLD = 0.012;
const MAX_DRIVERS = 6;

export function estimate(input: ScopeInput): Estimate {
  const { x, parts } = designRow(input);

  let log = SPEC.intercept;
  for (let i = 0; i < x.length; i++) log += x[i]! * SPEC.coef[i]!;

  // Empirical residual quantiles, not multiples of sigma: the residuals are
  // fat-tailed, so a normal-theory band misstates coverage in both directions.
  const q = SPEC.residual_quantiles;
  const drivers = parts
    .map((p, i) => ({ ...p, contribution: p.value * SPEC.coef[i]! }))
    .filter((p) => p.on && Math.abs(p.contribution) > DRIVER_THRESHOLD)
    .sort((a, b) => Math.abs(b.contribution) - Math.abs(a.contribution))
    .slice(0, MAX_DRIVERS)
    .map((p) => ({ name: p.name, multiplier: Math.exp(p.contribution) }));

  return {
    mid: Math.exp(log),
    lo50: Math.exp(log + q.p25!),
    hi50: Math.exp(log + q.p75!),
    lo80: Math.exp(log + q.p10!),
    hi80: Math.exp(log + q.p90!),
    drivers,
  };
}
